// AdminGlobalSearchOverlay — Cmd/Ctrl+K (or "/") command palette for the admin
// portal, mirroring the student GlobalSearchOverlay but on the Admin pane
// theme. A centered dark modal with a search input on top and grouped,
// keyboard-navigable results beneath: people, companies, cases & competitions,
// leads, invites. Backed by the `admin_global_search` RPC (one debounced,
// capped, admin-scoped round-trip — see lib/adminSearch.ts).
//
// The palette body (`AdminSearchPalette`) is presentational and exported so the
// /brand reference page can render it with static sample data; this file's
// default export wires it to the live RPC + router.

import type { Href } from "expo-router";
import { router } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ActivityIndicator,
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type ViewStyle,
} from "react-native";

import { COLORS, FONTS, RADII, STROKES } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";
import {
  adminGlobalSearch,
  groupResults,
  hrefForResult,
  type AdminSearchKind,
  type AdminSearchRow,
} from "../../lib/adminSearch";
import { useAuth } from "../../lib/authContext";
import { enterImpersonation } from "../../lib/impersonate";
import {
  IconClients,
  IconCompetitions,
  IconEnvelope,
  IconGavel,
  IconInquiries,
  IconSearch,
  IconStudents,
  IconUsers,
} from "./AdminIcons";

const THEME = PANE_THEMES.Admin;
const LINEN = THEME.textOnSurface;
const AMBER = THEME.accent;
const PANEL_BG = COLORS.OFF_BLACK.hex;
const TILE_BG = THEME.surfaceRaisedBg;
const DIM = THEME.textOnSurfaceDim;
const FAINT = THEME.textOnSurfaceFaint;
const BORDER = THEME.border;

function iconFor(kind: AdminSearchKind, color: string): ReactNode {
  switch (kind) {
    case "student":
      return <IconStudents color={color} />;
    case "judge":
      return <IconGavel color={color} />;
    case "company":
      return <IconClients color={color} />;
    case "competition":
      return <IconCompetitions color={color} />;
    case "inquiry":
      return <IconInquiries color={color} />;
    case "invite":
      return <IconEnvelope color={color} />;
    case "user":
    default:
      return <IconUsers color={color} />;
  }
}

/* ─────────────────────────  Presentational palette  ───────────────────────── */

export function AdminSearchPalette({
  visible,
  onClose,
  query,
  onQueryChange,
  loading,
  rows,
  selectedIndex,
  onHoverIndex,
  onActivate,
  viewAsFor,
  placeholder = "Search people, companies, cases…",
  autoFocus = true,
}: {
  visible: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  loading: boolean;
  rows: AdminSearchRow[];
  selectedIndex: number;
  onHoverIndex: (i: number) => void;
  onActivate: (row: AdminSearchRow) => void;
  /** When provided, returns a "View as" handler for rows that support it. */
  viewAsFor?: (row: AdminSearchRow) => (() => void) | null;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const isWeb = Platform.OS === "web";
  const inputRef = useRef<TextInput | null>(null);
  const groups = useMemo(() => groupResults(rows), [rows]);
  const hasQuery = query.trim().length >= 2;

  useEffect(() => {
    if (!visible || !isWeb) return;
    const id = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(id);
  }, [visible, isWeb]);

  if (!visible) return null;

  // Running flat index so keyboard selection lines up 1:1 with the rendered
  // rows across group boundaries.
  let flatIndex = -1;

  return (
    <RNModal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        accessibilityLabel="Close search"
        style={[
          {
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.55)",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingTop: 80,
            paddingHorizontal: 20,
          },
          isWeb
            ? ({
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                cursor: "default",
              } as object)
            : null,
        ]}
      >
        <Pressable
          onPress={(e) => e.stopPropagation?.()}
          style={[
            {
              width: "100%",
              maxWidth: 660,
              maxHeight: "78vh" as unknown as number,
              backgroundColor: PANEL_BG,
              borderRadius: RADII.lg,
              overflow: "hidden",
              borderWidth: STROKES.hairline,
              borderColor: BORDER,
            },
            isWeb
              ? ({
                  boxShadow: "0 30px 60px -20px rgba(0,0,0,0.7), 0 6px 16px -8px rgba(0,0,0,0.5)",
                  cursor: "default",
                } as object)
              : null,
          ]}
        >
          {/* Search input */}
          <View
            style={{
              paddingHorizontal: 18,
              paddingVertical: 14,
              borderBottomWidth: STROKES.hairline,
              borderBottomColor: BORDER,
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <IconSearch color={FAINT} size={17} />
            <TextInput
              ref={inputRef}
              value={query}
              onChangeText={onQueryChange}
              placeholder={placeholder}
              placeholderTextColor={FAINT}
              autoFocus={autoFocus}
              style={[
                {
                  flex: 1,
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  color: LINEN,
                  paddingVertical: 4,
                },
                isWeb ? ({ outlineStyle: "none" } as unknown as object) : null,
              ]}
            />
            {loading ? <ActivityIndicator size="small" color={AMBER} /> : <KbdHint label="ESC" />}
          </View>

          {/* Results */}
          <ScrollView
            style={{ maxHeight: 520 }}
            contentContainerStyle={{ paddingVertical: 6 }}
            keyboardShouldPersistTaps="handled"
          >
            {!hasQuery ? (
              <EmptyNote text="Type a name, email, company, or case to jump straight to it." />
            ) : groups.length === 0 && !loading ? (
              <EmptyNote text={`No matches for “${query.trim()}”.`} />
            ) : (
              groups.map((g) => (
                <View key={g.section.key} style={{ marginTop: 4 }}>
                  <GroupHeader label={g.section.label} />
                  {g.rows.map((row) => {
                    flatIndex += 1;
                    const idx = flatIndex;
                    return (
                      <ResultRow
                        key={`${row.kind}:${row.id}`}
                        row={row}
                        active={idx === selectedIndex}
                        onPress={() => onActivate(row)}
                        onHover={() => onHoverIndex(idx)}
                        onViewAs={viewAsFor ? viewAsFor(row) : null}
                      />
                    );
                  })}
                </View>
              ))
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

function GroupHeader({ label }: { label: string }) {
  return (
    <View style={{ paddingHorizontal: 18, paddingTop: 12, paddingBottom: 6 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: FAINT,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function EmptyNote({ text }: { text: string }) {
  return (
    <View style={{ paddingVertical: 34, paddingHorizontal: 24, alignItems: "center" }}>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 13,
          color: DIM,
          textAlign: "center",
        }}
      >
        {text}
      </Text>
    </View>
  );
}

function ResultRow({
  row,
  active,
  onPress,
  onHover,
  onViewAs,
}: {
  row: AdminSearchRow;
  active: boolean;
  onPress: () => void;
  onHover: () => void;
  onViewAs?: (() => void) | null;
}) {
  const isWeb = Platform.OS === "web";
  const iconColor = active ? AMBER : DIM;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={row.title}
      {...(isWeb ? ({ onHoverIn: onHover } as const) : {})}
      style={[
        {
          paddingHorizontal: 18,
          paddingVertical: 10,
          flexDirection: "row",
          alignItems: "center",
          gap: 13,
          backgroundColor: active ? THEME.accentTintSoft : "transparent",
          borderLeftWidth: STROKES.strong,
          borderLeftColor: active ? AMBER : "transparent",
        } as ViewStyle,
        isWeb ? ({ cursor: "pointer", transition: "background-color 0.12s ease" } as object) : null,
      ]}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: RADII.sm,
          backgroundColor: TILE_BG,
          borderWidth: STROKES.hairline,
          borderColor: BORDER,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {iconFor(row.kind, iconColor)}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          numberOfLines={1}
          style={{ fontFamily: FONTS.bodySemiBold, fontSize: 13.5, color: LINEN }}
        >
          {row.title}
        </Text>
        {row.subtitle ? (
          <Text
            numberOfLines={1}
            style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 11.5, color: DIM }}
          >
            {row.subtitle}
          </Text>
        ) : null}
      </View>
      {onViewAs ? (
        <Pressable
          onPress={(e) => {
            e.stopPropagation?.();
            onViewAs();
          }}
          accessibilityRole="button"
          accessibilityLabel={`View as ${row.title}`}
          style={[
            {
              paddingHorizontal: 8,
              paddingVertical: 3,
              borderRadius: RADII.full,
              borderWidth: STROKES.hairline,
              borderColor: THEME.accentBorderSoft,
            },
            isWeb ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            View as →
          </Text>
        </Pressable>
      ) : null}
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: active ? AMBER : FAINT }}>
        ↵
      </Text>
    </Pressable>
  );
}

function KbdHint({ label }: { label: string }) {
  return (
    <View
      style={{
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: RADII.xs,
        borderWidth: STROKES.hairline,
        borderColor: BORDER,
      }}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.2, color: FAINT }}>
        {label}
      </Text>
    </View>
  );
}

/* ─────────────────────────  Live container  ───────────────────────── */

export function AdminGlobalSearchOverlay({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const isWeb = Platform.OS === "web";
  const { role } = useAuth();
  const canViewAs = role === "super_admin";
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<AdminSearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const reqRef = useRef(0);

  // People results get a "View as" shortcut for super admins. Companies /
  // cases / leads / invites don't (there's no portal to view as).
  const viewAsFor = useCallback(
    (row: AdminSearchRow): (() => void) | null => {
      if (!canViewAs) return null;
      if (row.kind !== "student" && row.kind !== "judge" && row.kind !== "user") return null;
      return () => {
        onClose();
        const email = row.subtitle.includes("@") ? row.subtitle : null;
        void enterImpersonation({ userId: row.id, email });
      };
    },
    [canViewAs, onClose],
  );

  // Reset on open/close so the palette always reopens fresh.
  useEffect(() => {
    if (!visible) {
      setQuery("");
      setRows([]);
      setSelected(0);
      setLoading(false);
    }
  }, [visible]);

  // Debounced search. A monotonic request id guards against out-of-order
  // responses (a slow earlier query landing after a faster later one).
  useEffect(() => {
    if (!visible) return;
    const q = query.trim();
    if (q.length < 2) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const myReq = ++reqRef.current;
    const t = setTimeout(async () => {
      try {
        const result = await adminGlobalSearch(q);
        if (reqRef.current === myReq) {
          setRows(result);
          setSelected(0);
        }
      } catch {
        if (reqRef.current === myReq) setRows([]);
      } finally {
        if (reqRef.current === myReq) setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query, visible]);

  const activate = useCallback(
    (row: AdminSearchRow) => {
      onClose();
      const href = hrefForResult(row);
      // Defer so the modal animates out before the route swaps under it.
      setTimeout(() => router.push(href as Href), 30);
    },
    [onClose],
  );

  // Keyboard nav (web): ↑/↓ move the selection, Enter activates, Esc closes.
  // preventDefault on the arrows/Enter so the focused input doesn't also move
  // its caret or submit.
  useEffect(() => {
    if (!visible || !isWeb) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((s) => Math.min(rows.length - 1, s + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((s) => Math.max(0, s - 1));
      } else if (e.key === "Enter") {
        const row = rows[selected];
        if (row) {
          e.preventDefault();
          activate(row);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, isWeb, rows, selected, activate, onClose]);

  return (
    <AdminSearchPalette
      visible={visible}
      onClose={onClose}
      query={query}
      onQueryChange={setQuery}
      loading={loading}
      rows={rows}
      selectedIndex={selected}
      onHoverIndex={setSelected}
      onActivate={activate}
      viewAsFor={viewAsFor}
    />
  );
}
